import React, { useEffect, useMemo, useState } from "react";
import { Button, Drawer, Table } from "antd";
import { Chart } from "react-google-charts";
import { useStudentActivities } from "../../../hooks/Institute/useStudentActivities";
import { sortByNumericPrefix } from "../../../shared/utils/sort";
import { LoadingComponent } from "../../../shared/components/LoadingComponent";
import { RightOutlined, DownOutlined } from "@ant-design/icons";

import "./styles.scss";
import {
  BASE_URL,
  DOWNLOAD_INSTITUTE_ACTIVITY_REPORT,
} from "../../../routes/routeConstants/V2/apiRoutes";
import { StudentActivitiesFilterModel } from "../../../models/DashboardModel/DashboardFilterModel/dashboardFilter.model";
import { serialize } from "serializr";
const ClassDetails = ({ drawerVisible, onClose, branch, filters, ...rest }) => {
  const { getClassLogins, classLoginsData } = useStudentActivities();
  const [drawerLoading, setDrawerLoading] = useState(true);
  const [selectedRecord, setSelectedRecord] = useState<any>(undefined);
  const [sectionsLoading, setSectionsLoading] = useState(false);
  const [sectionsData, setSectionsData] = useState<any>(undefined);

  useEffect(() => {
    if (branch?.value) {
      getClassLogins({ ...filters, branch: branch.value });
    }
  }, [branch]);

  const chartData = useMemo(() => {
    if (!classLoginsData) return [["Grade", "Logins", "Not Logins"]];

    const { logins = {}, not_logins = {} } = classLoginsData;

    const allGrades = new Set([
      ...Object.keys(logins),
      ...Object.keys(not_logins),
    ]);
    const rows = Array.from(allGrades).map((grade) => [
      grade,
      logins[grade] || 0,
      not_logins[grade] || 0,
    ]);

    return sortByNumericPrefix([["Grade", "Logins", "Not Logins"], ...rows]);
  }, [classLoginsData]);

  useEffect(() => {
    if (drawerVisible) {
      setTimeout(() => {
        window.dispatchEvent(new Event("resize"));
      });
    }
  }, [drawerVisible]);

  useEffect(() => {
    if (drawerVisible) {
      setTimeout(() => {
        setDrawerLoading(false);
      }, 1000);
    }
  }, [drawerVisible]);

  const tableData = useMemo(
    () =>
      chartData.slice(1).map(([grade, logins, notLogins], index) => ({
        key: index,
        class: grade,
        logins,
        notLogins,
        active_count: logins + notLogins,
      })) || [],
    [chartData]
  );

  const tableColumns = [
    { title: "Class", dataIndex: "class", key: "class" },
    { title: "# of Logins", dataIndex: "logins", key: "logins" },
    { title: "# of Not Logins", dataIndex: "notLogins", key: "notLogins" },
    {
      title: "# of Active Students",
      dataIndex: "active_count",
      key: "active_count",
    },
  ];

  const sectionsDataColumns = [
    { title: "Section", dataIndex: "section", key: "section" },
    { title: "# of Logins", dataIndex: "logins", key: "logins" },
    { title: "# of Not Logins", dataIndex: "notLogins", key: "notLogins" },
    {
      title: "# of Active Students",
      dataIndex: "active_count",
      key: "active_count",
    },
  ];

  useEffect(() => {
    if (selectedRecord) {
      setSectionsLoading(true);
      const loginSectionsData =
        classLoginsData.login_sections_data[selectedRecord.class] || {};
      const NotLoginSectionsData =
        classLoginsData.not_logged_in_sections_data[selectedRecord.class] || {};

      const allSections = new Set([
        ...Object.keys(loginSectionsData),
        ...Object.keys(NotLoginSectionsData),
      ]);
      const rows = Array.from(allSections).map((section) => [
        section,
        loginSectionsData[section] || 0,
        NotLoginSectionsData[section] || 0,
      ]);

      const sortedData = sortByNumericPrefix([
        ["Grade", "Logins", "Not Logins"],
        ...rows,
      ]);
      // Remove header for table data
      const tableRows = sortedData
        .slice(1)
        .map(([section, logins, notLogins], index) => ({
          key: index,
          section: section,
          logins,
          notLogins,
          active_count: logins + notLogins,
        }));
      setSectionsData(tableRows);
      setSectionsLoading(false);
    } else {
      setSectionsData(undefined);
    }
  }, [selectedRecord, classLoginsData]);

  const downloadStudentReport = () => {
    var element = document.createElement("a");
    const user = JSON.parse(localStorage.getItem("token") || "");
    const payload = serialize(StudentActivitiesFilterModel, {
      ...filters,
      branch: branch.value,
    });
    const filtered = Object.fromEntries(
      Object.entries(payload).filter(([_, value]) => value !== undefined)
    );
    const params = new URLSearchParams({
      access_token: user.access_token,
      ...filtered,
    });
    element.href = `${BASE_URL}${DOWNLOAD_INSTITUTE_ACTIVITY_REPORT}?${params.toString()}`;
    element.click();
  };

  return (
    <>
      <Drawer
        title={`${branch?.label ?? ""} Login Statistics`}
        placement="right"
        width={1000}
        onClose={() => onClose(false)}
        open={drawerVisible}
        {...rest}
      >
        {drawerLoading ? (
          <div>
            <LoadingComponent title="Loading chart..." />
          </div>
        ) : (
          chartData.length > 1 && (
            <div style={{ width: "100%" }}>
              <Chart
                chartType="ColumnChart"
                width="100%"
                height="500px"
                data={chartData}
                options={{
                  chart: { title: "Logins vs Not Logins" },
                  legend: { position: "bottom" },
                  colors: ["#1890ff", "#f5222d"],
                }}
              />
            </div>
          )
        )}
        <div className="download-report-button">
          <Button
            className="download-btn"
            onClick={() => downloadStudentReport()}
          >
            <p className="btn-text">Download Data</p>
          </Button>
        </div>

        <Table
          columns={tableColumns}
          dataSource={tableData}
          pagination={false}
          expandable={{
            expandedRowRender: (record) => (
              <p style={{ margin: 0 }}>
                <Table
                  columns={sectionsDataColumns}
                  dataSource={sectionsData}
                  pagination={false}
                  style={{ marginBottom: 24 }}
                />
              </p>
            ),
            onExpand: (expanded, record) => {
              debugger;
              setSelectedRecord(record);
              setSectionsLoading(true);
            },
            expandIcon: ({ expanded, onExpand, record }) =>
              expanded ? (
                <DownOutlined
                  className="expandable__icon"
                  onClick={(e) => onExpand(record, e)}
                />
              ) : (
                <RightOutlined
                  className="expandable__icon"
                  onClick={(e) => onExpand(record, e)}
                />
              ),
          }}
          style={{ marginBottom: 24 }}
        />
      </Drawer>
    </>
  );
};

export default ClassDetails;
